import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Plus, X } from 'lucide-react'
import type { Quote, TradeSide } from '@shared/types'
import type { PfAccount, PortfolioLive } from '@shared/portfolio'
import { api, ordersApi, workersApi } from '../../lib/api'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'
import { fmtMoney, fmtPrice } from '../../lib/format'
import { displaySymbol, normalizeSymbol } from '../../lib/symbol'
import { useExtendedQuotes, useLiveQuotes, useMarketSession } from '../../hooks/useMarketData'
import { SymbolSearch } from './SymbolSearch'
import { OrderCard } from '../workers/OrderCard'
import { WorkerCard } from '../workers/WorkerCard'

/** Recording a trade made somewhere else (no order is sent). */
const RECORD = 'record'

const TYPES = [
  ['MKT', 'Market', 'Now, at the best price'],
  ['LMT', 'Limit', 'At your price or better'],
  ['STP', 'Stop', 'A market order once the price gets there'],
  ['STP LMT', 'Stop limit', 'A limit order once the price gets there'],
  ['TRAIL', 'Trailing stop', 'A stop that follows the price'],
  ['TRAIL LIMIT', 'Trailing stop limit', 'A trailing stop that sends a limit'],
  ['MIT', 'Market if touched', 'A market order once the price touches a level'],
  ['LIT', 'Limit if touched', 'A limit order once the price touches a level'],
  ['MOC', 'Market on close', 'At the closing price'],
] as const
type OrderKind = (typeof TYPES)[number][0]
const WORKER_ENTRY = new Set<OrderKind>(['MKT', 'LMT', 'STP', 'STP LMT'])

type SizeMode = 'shares' | 'usd' | 'pct' | 'risk'
interface Target {
  price: string
  pct: string
}
interface StopRow {
  price: string
  pct: string
  kind: 'stop' | 'limit' | 'trail'
  /** limit: the worst fill price · trail: the distance. */
  extra: string
  trailUnit: 'usd' | 'pct'
}
type When = 'tp' | 'above' | 'below' | 'sessions' | 'minutes' | 'earnings' | 'time' | 'daily'
type Then = 'breakeven' | 'stopTo' | 'trail' | 'close' | 'reduce' | 'cancelEntry' | 'notify'
interface RuleRow {
  when: When
  whenVal: string
  then: Then
  thenVal: string
  thenUnit: 'usd' | 'pct' | 'atr'
}
const WHEN: [When, string, string][] = [
  ['tp', 'a target fills', 'target #'],
  ['above', 'the price rises to', 'price'],
  ['below', 'the price falls to', 'price'],
  ['sessions', 'sessions after the buy', 'sessions'],
  ['minutes', 'minutes after the buy', 'minutes'],
  ['earnings', 'before the next earnings', ''],
  ['time', 'a date and time (New York)', 'YYYY-MM-DD HH:MM'],
  ['daily', 'every day at (New York)', 'HH:MM'],
]
const THEN: [Then, string, string][] = [
  ['breakeven', 'move the stops to break-even', ''],
  ['stopTo', 'move the stops to', 'price'],
  ['trail', 'trail the stops by', 'distance'],
  ['close', 'close the position', ''],
  ['reduce', 'sell part of it', '% of the shares'],
  ['cancelEntry', 'cancel what has not been bought', ''],
  ['notify', 'tell me', 'message'],
]

const numOf = (s: string) => {
  const n = Number(String(s).replace(',', '.').trim())
  return Number.isFinite(n) && n > 0 ? n : null
}
/** A price typed as a number, or relative to the entry: "+4%", "-2.5%". */
function priceOf(s: string, ref: number): number | null {
  const t = String(s).replace(',', '.').trim()
  const m = /^([+-]?\d+(?:\.\d+)?)\s*%$/.exec(t)
  if (m) return ref > 0 ? Math.round(ref * (1 + Number(m[1]) / 100) * 100) / 100 : null
  return numOf(t)
}
const portion = (pct: number) => (Number.isInteger(pct) ? { kind: 'portion' as const, num: pct, den: 100 } : { kind: 'portion' as const, num: Math.round(pct * 10), den: 1000 })
const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(6).replace(/0+$/, '').replace(/\.$/, ''))
const etNow = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/New_York' }).slice(0, 16)

const field = 'num w-full min-w-0 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-1.5 text-[12.5px] text-fg placeholder:text-fg-faint focus:border-[var(--line)] focus:outline-none'
const select = 'min-w-0 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2 py-1.5 text-[12px] text-fg focus:outline-none'
const menu = 'absolute right-0 top-9 z-40 rounded-lg border border-[var(--line)] bg-[var(--color-surface-3)] p-1 shadow-[var(--e-3)]'
const chip = 'rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10.5px] text-fg-subtle hover:bg-[var(--color-surface-3)] hover:text-fg'

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-lg bg-[var(--color-inset)] px-3 py-2">
      <span className="shrink-0 text-[12px] text-fg-subtle">{label}</span>
      {children}
    </label>
  )
}
function Fold({ title, hint, open, onToggle, count, children }: { title: string; hint: string; open: boolean; onToggle: () => void; count?: number; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[var(--line-soft)]">
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left">
        <span className="text-[12.5px] font-medium text-fg">
          {title}
          {count ? <span className="num ml-1.5 text-[11px] text-fg-muted">{count}</span> : null}
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-fg-faint">
          {!open && hint}
          <ChevronDown size={13} className={cn('transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && <div className="space-y-2 border-t border-[var(--line-soft)] p-2.5">{children}</div>}
    </div>
  )
}

/**
 * The portfolio's order ticket. To a connected account it sends anything the app can run — every
 * order type, a ladder of targets and stops, and rules that manage the position afterwards — always
 * through the system's own card: Review compiles the exact orders, risk and checks, Approve sends
 * them. For shares kept at a broker that is not connected it records a trade by hand.
 */
export function OrderTicket({
  symbol,
  onSymbolChange,
  portfolio,
  scope,
  onChanged,
}: {
  symbol: string
  onSymbolChange: (s: string) => void
  portfolio: PortfolioLive | null
  /** The account the page is narrowed to, if any. */
  scope: string | null
  onChanged: () => void
}) {
  const trade = useStore((s) => s.trade)
  const setLevels = useStore((s) => s.setLevels)
  const tradable = useMemo(() => (portfolio?.accounts ?? []).filter((a) => a.tradable), [portfolio])
  const [dest, setDest] = useState<string>(RECORD)
  const [destOpen, setDestOpen] = useState(false)
  /** buy / sell an order — or manage: put targets, stops and rules on shares already held (no order to enter). */
  const [mode, setMode] = useState<'buy' | 'sell' | 'manage'>('buy')
  const side: TradeSide = mode === 'sell' ? 'sell' : 'buy'
  const [kind, setKind] = useState<OrderKind>('MKT')
  const [typeOpen, setTypeOpen] = useState(false)
  const [sizeMode, setSizeMode] = useState<SizeMode>('shares')
  const [size, setSize] = useState('')
  const [limit, setLimit] = useState('')
  const [stop, setStop] = useState('')
  const [trailVal, setTrailVal] = useState('')
  const [trailUnit, setTrailUnit] = useState<'usd' | 'pct'>('pct')
  const [offset, setOffset] = useState('')
  const [tif, setTif] = useState<'DAY' | 'GTC' | 'GTD'>('DAY')
  const [goodTill, setGoodTill] = useState('')
  const [ext, setExt] = useState(false)
  const [targets, setTargets] = useState<Target[]>([])
  const [stops, setStops] = useState<StopRow[]>([])
  const [rules, setRules] = useState<RuleRow[]>([])
  const [insurance, setInsurance] = useState<'default' | 'off' | '75' | '90' | '95'>('default')
  const [exitExt, setExitExt] = useState(false)
  const [open, setOpen] = useState<{ exits: boolean; rules: boolean; notes: boolean }>({ exits: false, rules: false, notes: false })
  const [sl, setSl] = useState('')
  const [tp, setTp] = useState('')
  const [fee, setFee] = useState('')
  const [note, setNote] = useState('')
  const [quote, setQuote] = useState<Quote | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)
  const [card, setCard] = useState<{ kind: 'order' | 'worker'; id: string } | null>(null)

  // Where the order goes: the account the page is narrowed to when it can trade, else the first that can.
  const tradableKey = tradable.map((a) => a.id).join(',')
  useEffect(() => {
    const want = scope && tradable.some((a) => a.id === scope) ? scope : scope === 'manual' ? RECORD : (tradable[0]?.id ?? RECORD)
    setDest((cur) => (cur !== RECORD && tradable.some((a) => a.id === cur) && !scope ? cur : want))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradableKey, scope])

  const account: PfAccount | null = tradable.find((a) => a.id === dest) ?? null
  const recording = !account
  const manage = mode === 'manage' && !recording
  const buy = side === 'buy'

  const norm = symbol.trim().toUpperCase()
  const session = useMarketSession()
  const extMap = useExtendedQuotes(norm ? [norm] : [], session)
  const liveMap = useLiveQuotes(norm ? [norm] : [])
  const display = norm ? displaySymbol(norm) : '—'
  const fullSym = norm ? normalizeSymbol(norm) : ''
  const holding = portfolio?.holdings.find((h) => h.code === fullSym) ?? null
  const inAccount = account ? (holding?.parts.find((p) => p.account === account.id) ?? null) : null
  const workerShares = inAccount?.workers.reduce((a, w) => a + w.open, 0) ?? 0
  const sellable = Math.max(0, (inAccount?.quantity ?? 0) - workerShares)
  // Robinhood holds fractions of a share and a worker can manage them; IBKR's API takes whole shares only.
  const fractions = account?.broker === 'robinhood'
  const manageable = fractions ? Math.round(sellable * 1e6) / 1e6 : Math.floor(sellable + 1e-9)

  useEffect(() => {
    setMsg(null)
    if (!norm) {
      setQuote(null)
      return
    }
    let alive = true
    api
      .quote(norm)
      .then((q) => alive && setQuote(q))
      .catch(() => alive && setQuote(null))
    return () => {
      alive = false
    }
  }, [norm])
  useEffect(() => {
    setSl(holding?.stopLoss != null ? String(holding.stopLoss) : '')
    setTp(holding?.takeProfit != null ? String(holding.takeProfit) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSym, holding?.stopLoss, holding?.takeProfit])
  // A sale has no exits of its own; recording knows two kinds of trade.
  useEffect(() => {
    if (recording && kind !== 'MKT' && kind !== 'LMT') setKind('MKT')
    if (recording && (sizeMode === 'pct' || sizeMode === 'risk')) setSizeMode('shares')
    if (!buy && sizeMode === 'risk') setSizeMode('shares')
    if (mode === 'manage') {
      if (recording || manageable <= 0) setMode('buy')
      else {
        setSizeMode('shares')
        setKind('MKT')
        setSize(String(manageable))
        setOpen((o) => ({ ...o, exits: true }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, buy, mode, fullSym, manageable])

  // A held symbol's price comes with the portfolio itself (the broker's feed); otherwise the market data feed.
  const last = holding?.last ?? liveMap[fullSym]?.price ?? extMap[fullSym]?.price ?? quote?.price ?? 0
  const needsLimit = kind === 'LMT' || kind === 'STP LMT' || kind === 'LIT'
  const needsStop = kind === 'STP' || kind === 'STP LMT'
  const needsTouch = kind === 'MIT' || kind === 'LIT'
  const trailing = kind === 'TRAIL' || kind === 'TRAIL LIMIT'
  // The price the order is sized and its exits measured against.
  const ref = (needsLimit && kind !== 'LIT' ? numOf(limit) : null) ?? (needsStop ? numOf(stop) : null) ?? (needsTouch ? numOf(stop) : null) ?? (last > 0 ? last : 0)

  const exitsOn = !recording && buy && (targets.length > 0 || stops.length > 0)
  // A plan with a ladder, a special stop, rules, risk sizing or its own insurance runs as a trade worker.
  const asWorker =
    !recording &&
    buy &&
    (manage ||
      targets.length > 1 || stops.length > 1 || stops.some((s) => s.kind !== 'stop') || rules.length > 0 || sizeMode === 'risk' || (insurance !== 'default' && exitsOn) || (exitExt && exitsOn))

  const stopPx = stops.map((s) => priceOf(s.price, ref)).filter((x): x is number => x != null)
  const nearestStop = stopPx.length ? Math.max(...stopPx) : null
  const sizeNum = numOf(size)
  const shares = (() => {
    if (!sizeNum || !(ref > 0)) return sizeMode === 'shares' ? (sizeNum ?? 0) : 0
    if (sizeMode === 'shares') return sizeNum
    if (sizeMode === 'usd') return recording ? sizeNum / ref : Math.floor(sizeNum / ref)
    if (sizeMode === 'pct') return buy ? Math.floor((((account?.buyingPower ?? 0) * sizeNum) / 100) / ref) : (sellable * sizeNum) / 100
    return nearestStop != null && ref > nearestStop ? Math.floor(sizeNum / (ref - nearestStop)) : 0
  })()
  const total = shares * ref

  // What the ladder risks and aims for, on the estimated size.
  const plan = (() => {
    if (!exitsOn || !(shares > 0) || !(ref > 0)) return null
    let left = 1
    let risk = 0
    stops.forEach((s, i) => {
      const p = priceOf(s.price, ref)
      const share = i === stops.length - 1 ? left : Math.min(left, (numOf(s.pct) ?? 0) / 100)
      left -= share
      if (p != null) risk += shares * share * (ref - p)
    })
    left = 1
    let reward = 0
    targets.forEach((t, i) => {
      const p = priceOf(t.price, ref)
      const share = i === targets.length - 1 && !numOf(t.pct) ? left : Math.min(left, (numOf(t.pct) ?? 0) / 100)
      left -= share
      if (p != null) reward += shares * share * (p - ref)
    })
    return { risk, reward }
  })()

  const problems: string[] = []
  if (!recording) {
    if (asWorker && !WORKER_ENTRY.has(kind)) problems.push('A ladder or rules need a market, limit, stop or stop-limit entry.')
    if (ext && kind !== 'LMT' && kind !== 'LIT') problems.push('Extended hours: limit orders only.')
    if (tif === 'GTD' && !/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(goodTill)) problems.push('Good-till needs a date and time.')
    if (sizeMode === 'risk' && nearestStop == null) problems.push('Sizing by risk needs a stop below.')
    if (manage && !targets.length && !stops.length && !rules.length) problems.push('Add a target, a stop or a rule for these shares.')
    if (manage && (sizeNum ?? 0) > manageable) problems.push(`You can hand over at most ${manageable} ${fractions ? '' : 'whole '}share${manageable === 1 ? '' : 's'} here.`)
  }
  const ready =
    !!norm &&
    !!sizeNum &&
    (kind === 'MKT' || kind === 'MOC' || ref > 0) &&
    (!needsLimit || !!numOf(limit)) &&
    (!(needsStop || needsTouch) || !!numOf(stop)) &&
    (!trailing || !!numOf(trailVal)) &&
    !account?.blocked &&
    !problems.length

  /** The order as the order desk takes it (one order, optionally with one stop and one target). */
  const ticketSpec = () => {
    const quantity =
      sizeMode === 'usd' ? { kind: 'notional', usd: sizeNum } : sizeMode === 'pct' ? (buy ? { kind: 'notional', usd: ((account?.buyingPower ?? 0) * sizeNum!) / 100 } : sizeNum! >= 100 ? { kind: 'all' } : { kind: 'percent', pct: sizeNum }) : { kind: 'shares', shares: sizeNum }
    const t0 = targets[0] ? priceOf(targets[0].price, ref) : null
    const s0 = stops[0] ? priceOf(stops[0].price, ref) : null
    return {
      symbol: display,
      action: buy ? 'BUY' : 'SELL',
      quantity,
      orderType: kind,
      ...(needsLimit ? { limitPrice: numOf(limit) } : {}),
      ...(needsStop ? { stopPrice: numOf(stop) } : {}),
      ...(needsTouch ? { triggerPrice: numOf(stop) } : {}),
      ...(trailing ? { trail: trailUnit === 'usd' ? { amount: numOf(trailVal) } : { percent: numOf(trailVal) }, ...(numOf(stop) ? { stopPrice: numOf(stop) } : {}) } : {}),
      ...(kind === 'TRAIL LIMIT' ? { limitOffset: numOf(offset) ?? 0.05 } : {}),
      tif,
      ...(tif === 'GTD' ? { goodTill: goodTill.replace('T', ' ').slice(0, 16) } : {}),
      outsideRth: ext,
      ...(buy && (t0 != null || s0 != null) ? { bracket: { ...(s0 != null ? { stopLoss: s0 } : {}), ...(t0 != null ? { takeProfit: t0 } : {}) } } : {}),
      broker: account!.broker,
    }
  }
  /** The plan as a trade worker takes it (a ladder of targets and stops, rules, insurance). */
  const workerSpec = (): { spec: unknown } | { error: string } => {
    const sizing =
      sizeMode === 'shares' ? { kind: 'shares', shares: manage && fractions ? Math.round(sizeNum! * 1e6) / 1e6 : Math.floor(sizeNum!) } : sizeMode === 'usd' ? { kind: 'notional', usd: sizeNum } : sizeMode === 'pct' ? { kind: 'notional', usd: ((account?.buyingPower ?? 0) * sizeNum!) / 100 } : { kind: 'risk', usd: sizeNum }
    const takeProfits: unknown[] = []
    for (let i = 0; i < targets.length; i++) {
      const p = priceOf(targets[i].price, ref)
      if (p == null) return { error: `Target ${i + 1} needs a price.` }
      const pct = numOf(targets[i].pct)
      if (!pct && i < targets.length - 1) return { error: `Target ${i + 1} needs its share of the position (%).` }
      takeProfits.push({ price: p, qty: pct && pct < 100 ? portion(pct) : { kind: 'rest' } })
    }
    const stopLosses: unknown[] = []
    for (let i = 0; i < stops.length; i++) {
      const s = stops[i]
      const p = priceOf(s.price, ref)
      if (p == null) return { error: `Stop ${i + 1} needs a price.` }
      const lastRow = i === stops.length - 1
      const pct = numOf(s.pct)
      if (!lastRow && !pct) return { error: `Stop ${i + 1} needs its share of the position (%) — the last stop takes the rest.` }
      const extra = numOf(s.extra)
      if (s.kind !== 'stop' && !extra) return { error: `Stop ${i + 1}: give the ${s.kind === 'limit' ? 'limit price' : 'trailing distance'}.` }
      stopLosses.push({
        price: p,
        qty: lastRow ? { kind: 'rest' } : portion(pct!),
        ...(s.kind === 'limit' ? { limitPrice: extra } : {}),
        ...(s.kind === 'trail' ? { trail: s.trailUnit === 'usd' ? { amount: extra } : { percent: extra } } : {}),
      })
    }
    const out: unknown[] = []
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i]
      const v = numOf(r.whenVal)
      let when: unknown
      if (r.when === 'tp') when = { on: 'fill', what: 'tp', tier: Math.max(1, Math.round(v ?? 1)) }
      else if (r.when === 'above' || r.when === 'below') {
        const p = priceOf(r.whenVal, ref)
        if (p == null) return { error: `Rule ${i + 1}: give the price.` }
        when = { on: 'price', op: r.when === 'above' ? '>=' : '<=', value: p }
      } else if (r.when === 'sessions') when = { on: 'sessions', since: 'entryFilled', count: Math.max(1, Math.round(v ?? 1)), at: 'close' }
      else if (r.when === 'minutes') when = { on: 'elapsed', since: 'entryFilled', minutes: Math.max(1, Math.round(v ?? 1)) }
      else if (r.when === 'earnings') when = { on: 'earnings', timing: 'before' }
      else if (r.when === 'time') {
        if (!/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(r.whenVal)) return { error: `Rule ${i + 1}: the time is YYYY-MM-DD HH:MM (New York).` }
        when = { on: 'time', at: r.whenVal.replace('T', ' ').slice(0, 16) }
      } else {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(r.whenVal.trim())) return { error: `Rule ${i + 1}: the time is HH:MM (New York).` }
        when = { on: 'daily', at: r.whenVal.trim() }
      }
      const tv = numOf(r.thenVal)
      let then: unknown
      if (r.then === 'breakeven') then = { do: 'moveStop', to: 'breakeven' }
      else if (r.then === 'stopTo') {
        const p = priceOf(r.thenVal, ref)
        if (p == null) return { error: `Rule ${i + 1}: give the new stop price.` }
        then = { do: 'moveStop', to: p }
      } else if (r.then === 'trail') {
        if (!tv) return { error: `Rule ${i + 1}: give the trailing distance.` }
        then = { do: 'trail', [r.thenUnit]: tv }
      } else if (r.then === 'close') then = { do: 'close' }
      else if (r.then === 'reduce') {
        if (!tv || tv > 100) return { error: `Rule ${i + 1}: how much to sell (1–100%).` }
        then = { do: 'reduce', percent: tv }
      } else if (r.then === 'cancelEntry') then = { do: 'cancelEntry' }
      else then = { do: 'notify', message: r.thenVal.trim() || `${display}: rule ${i + 1} fired` }
      out.push({ id: `r${i + 1}`, when, then: [then] })
    }
    return {
      spec: {
        name: `${display} · ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
        symbol: display,
        side: 'long',
        sizing,
        entry: manage ? { orderType: 'MKT', tif: 'DAY' } : {
          orderType: kind,
          ...(kind === 'LMT' || kind === 'STP LMT' ? { limitPrice: numOf(limit) } : {}),
          ...(needsStop ? { stopPrice: numOf(stop) } : {}),
          tif,
          ...(tif === 'GTD' ? { goodTill: goodTill.replace('T', ' ').slice(0, 16) } : {}),
          outsideRth: ext,
        },
        takeProfits,
        stopLosses,
        ...(stops.length ? {} : { noStop: true }),
        ...(manage ? { adopt: { avgCost: inAccount?.avgCost ?? last } } : {}),
        exitOutsideRth: exitExt,
        rules: out,
        ...(insurance === 'default' ? {} : insurance === 'off' ? { insurance: { on: false } } : { insurance: { on: true, level: Number(insurance) } }),
        note: 'Entered on the order ticket.',
        broker: account!.broker,
      },
    }
  }

  const review = async () => {
    if (!account) return
    setBusy(true)
    setMsg(null)
    try {
      if (asWorker) {
        const w = workerSpec()
        if ('error' in w) throw new Error(w.error)
        const d = await workersApi.draft(w.spec)
        setCard({ kind: 'worker', id: d.id })
      } else {
        const d = await ordersApi.draft(ticketSpec())
        setCard({ kind: 'order', id: d.id })
      }
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'The order could not be prepared' })
    } finally {
      setBusy(false)
    }
  }
  /** Closing the card without approving discards the draft (an approved one is untouched: reject refuses it). */
  const closeCard = () => {
    const c = card
    setCard(null)
    if (c) void (c.kind === 'order' ? ordersApi.reject(c.id) : workersApi.reject(c.id)).catch(() => {})
    setTimeout(onChanged, 300)
  }
  const record = async () => {
    setBusy(true)
    try {
      const price = kind === 'LMT' ? (numOf(limit) ?? last) : last
      await trade({ symbol: norm, side, quantity: shares, price, fee: Number(fee) || 0, note: note || undefined, stopLoss: sl ? Number(sl) : null, takeProfit: tp ? Number(tp) : null })
      setSize('')
      setNote('')
      setMsg({ tone: 'ok', text: 'Recorded.' })
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  const sizeModes: [SizeMode, string][] = manage ? [['shares', 'Shares to manage']] : recording ? [['shares', 'Shares'], ['usd', 'USD']] : buy ? [['shares', 'Shares'], ['usd', 'USD'], ['pct', '% cash'], ['risk', 'Risk $']] : [['shares', 'Shares'], ['pct', '% held']]
  const kinds = recording ? TYPES.filter(([k]) => k === 'MKT' || k === 'LMT') : TYPES
  const kindLabel = TYPES.find(([k]) => k === kind)![1]
  const nudge = (set: (v: string) => void, pct: number) => last > 0 && set((last * (1 + pct / 100)).toFixed(2))

  return (
    <div className="card overflow-hidden" style={{ borderRadius: 'var(--radius-lg)' }}>
      {tradable.length > 0 && (
        <div className="flex items-center justify-between gap-2 border-b border-[var(--line-soft)] px-3 py-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">Order to</span>
          <div className="relative">
            <button onClick={() => setDestOpen((o) => !o)} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md px-1.5 py-1 text-[12.5px] font-medium text-fg hover:bg-[var(--color-surface-2)]">
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', account && !account.blocked ? 'bg-up' : 'bg-flat')} />
              <span className="truncate">{account ? account.name : 'Record only'}</span>
              {account && <span className="num text-fg-faint">••{account.mask?.slice(-4)}</span>}
              <ChevronDown size={13} className="shrink-0 text-fg-subtle" />
            </button>
            {destOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setDestOpen(false)} />
                <div className={cn(menu, 'w-64')}>
                  {tradable.map((a) => (
                    <button key={a.id} onClick={() => { setDest(a.id); setDestOpen(false) }} className={cn('block w-full rounded px-2 py-1.5 text-left hover:bg-[var(--color-surface-2)]', dest === a.id ? 'text-fg' : 'text-fg-muted')}>
                      <div className="flex items-center justify-between gap-2 text-[12.5px]">
                        <span className="truncate font-medium">{a.name}</span>
                        <span className={cn('shrink-0 text-[9.5px] font-semibold uppercase', a.paper ? 'text-fg-subtle' : 'text-down')}>{a.paper ? 'paper' : 'real money'}</span>
                      </div>
                      <div className="num text-[11px] text-fg-faint">Buying power {fmtMoney(a.buyingPower)}</div>
                    </button>
                  ))}
                  <button onClick={() => { setDest(RECORD); setDestOpen(false) }} className={cn('mt-0.5 block w-full rounded border-t border-[var(--line-soft)] px-2 py-1.5 text-left hover:bg-[var(--color-surface-2)]', recording ? 'text-fg' : 'text-fg-muted')}>
                    <div className="text-[12.5px] font-medium">Record only</div>
                    <div className="text-[11px] text-fg-faint">A trade you made at a broker that is not connected — no order is sent.</div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="p-3 pb-1.5">
        <SymbolSearch value={symbol} onSelect={onSymbolChange} big placeholder="Search a stock — symbol or name" />
      </div>
      <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-1">
        <div className="inline-flex rounded-lg bg-[var(--color-inset)] p-0.5">
          {(['buy', 'sell'] as const).map((s) => (
            <button key={s} onClick={() => setMode(s)} className={cn('rounded-md px-4 py-1.5 text-[13px] font-semibold capitalize transition-colors', mode === s ? (s === 'buy' ? 'bg-up text-[var(--color-base)]' : 'bg-down text-[var(--color-base)]') : 'text-fg-subtle hover:text-fg')}>
              {s}
            </button>
          ))}
          {!recording && (
            <button
              onClick={() => setMode('manage')}
              disabled={manageable <= 0}
              title={manageable <= 0 ? (fractions ? 'For shares you hold in this account that no worker manages yet' : 'For shares you hold in this account (at least one whole share that no worker manages yet)') : 'Put targets, stops and rules on the shares you already hold — nothing is bought'}
              className={cn('rounded-md px-4 py-1.5 text-[13px] font-semibold transition-colors disabled:opacity-35', manage ? 'bg-[var(--color-accent)] text-[var(--color-base)]' : 'text-fg-subtle hover:text-fg')}
            >
              Manage
            </button>
          )}
        </div>
        <div className={cn('relative', manage && 'hidden')}>
          <button onClick={() => setTypeOpen((o) => !o)} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[13px] font-medium text-fg-muted hover:text-fg">
            {kindLabel} <ChevronDown size={14} />
          </button>
          {typeOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setTypeOpen(false)} />
              <div className={cn(menu, 'w-64')}>
                {kinds.map(([k, label, hint]) => (
                  <button
                    key={k}
                    onClick={() => {
                      setKind(k)
                      if ((k === 'LMT' || k === 'STP LMT' || k === 'LIT') && !limit && last) setLimit(last.toFixed(2))
                      setTypeOpen(false)
                    }}
                    className={cn('block w-full rounded px-2 py-1.5 text-left hover:bg-[var(--color-surface-2)]', kind === k ? 'text-fg' : 'text-fg-muted')}
                  >
                    <div className="text-[12.5px] font-medium">{label}</div>
                    <div className="text-[11px] text-fg-faint">{hint}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2.5 px-3 pb-3">
        {/* how much */}
        <div className="rounded-xl bg-[var(--color-inset)] px-3 py-2.5">
          <div className="flex items-center justify-between">
            <div className="inline-flex gap-0.5">
              {sizeModes.map(([m, label]) => (
                <button key={m} onClick={() => setSizeMode(m)} className={cn('rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide', sizeMode === m ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg')}>
                  {label}
                </button>
              ))}
            </div>
            {sizeMode === 'pct' && (
              <span className="flex gap-1">
                {[25, 50, 75, 100].map((p) => (
                  <button key={p} onClick={() => setSize(String(p))} className={chip}>
                    {p}%
                  </button>
                ))}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <input value={size} onChange={(e) => setSize(e.target.value)} placeholder="0" inputMode="decimal" className="num min-w-0 flex-1 bg-transparent text-[26px] font-semibold text-fg placeholder:text-fg-faint focus:outline-none" />
            <span className="text-[18px] font-medium text-fg-subtle">{sizeMode === 'shares' ? display : sizeMode === 'pct' ? '%' : 'USD'}</span>
          </div>
          <div className="mt-0.5 text-[11.5px] text-fg-subtle">
            {sizeMode !== 'shares' && shares > 0 && <span className="num text-fg-muted">≈ {qtyText(Math.round(shares * 1e6) / 1e6)} {display} · </span>}
            {account ? (
              <>
                In this account <span className="num text-fg-muted">{qtyText(inAccount?.quantity ?? 0)}</span>
                {workerShares > 0 && <span className="num"> ({qtyText(workerShares)} with a worker)</span>}
                {holding && holding.quantity !== (inAccount?.quantity ?? 0) ? <span className="num"> · {qtyText(holding.quantity)} in all</span> : null}
              </>
            ) : (
              <>
                Holding <span className="num text-fg-muted">{qtyText(holding?.quantity ?? 0)}</span>
                {holding?.avgCost != null ? <span className="num"> · avg {fmtPrice(holding.avgCost, '$')}</span> : null}
              </>
            )}
          </div>
        </div>

        {/* at what price */}
        <div className="flex items-center justify-between rounded-lg bg-[var(--color-inset)] px-3 py-2">
          <span className="text-[12px] text-fg-subtle">Market price</span>
          <span className="flex items-center gap-1.5">
            {session !== 'regular' && last > 0 && <span className="rounded bg-[var(--color-surface-3)] px-1 text-[9px] font-semibold uppercase text-fg-subtle">{session === 'pre' ? 'PRE' : session === 'after' ? 'AH' : 'closed'}</span>}
            <span className="num text-[14px] font-medium text-fg">{fmtPrice(last, '$')}</span>
          </span>
        </div>
        {(needsStop || needsTouch) && (
          <Row label={needsTouch ? 'When the price touches' : 'Stop price'}>
            <span className="flex items-center gap-1.5">
              <button onClick={() => nudge(setStop, buy === needsTouch ? -1 : 1)} className={chip}>{buy === needsTouch ? '−1%' : '+1%'}</button>
              <input value={stop} onChange={(e) => setStop(e.target.value)} inputMode="decimal" placeholder="0.00" className="num w-24 bg-transparent text-right text-[14px] font-medium text-fg focus:outline-none" />
            </span>
          </Row>
        )}
        {needsLimit && (
          <Row label="Limit price">
            <span className="flex items-center gap-1.5">
              <button onClick={() => nudge(setLimit, 0)} className={chip}>last</button>
              <button onClick={() => nudge(setLimit, buy ? -1 : 1)} className={chip}>{buy ? '−1%' : '+1%'}</button>
              <input value={limit} onChange={(e) => setLimit(e.target.value)} inputMode="decimal" placeholder={last ? last.toFixed(2) : '0.00'} className="num w-24 bg-transparent text-right text-[14px] font-medium text-fg focus:outline-none" />
            </span>
          </Row>
        )}
        {trailing && (
          <>
            <Row label="Trails the price by">
              <span className="flex items-center gap-1.5">
                <input value={trailVal} onChange={(e) => setTrailVal(e.target.value)} inputMode="decimal" placeholder={trailUnit === 'pct' ? '2' : '1.50'} className="num w-20 bg-transparent text-right text-[14px] font-medium text-fg focus:outline-none" />
                <select value={trailUnit} onChange={(e) => setTrailUnit(e.target.value as 'usd' | 'pct')} className={select}>
                  <option value="pct">%</option>
                  <option value="usd">$</option>
                </select>
              </span>
            </Row>
            {kind === 'TRAIL LIMIT' && (
              <Row label="Limit this far past the stop">
                <input value={offset} onChange={(e) => setOffset(e.target.value)} inputMode="decimal" placeholder="0.05" className="num w-24 bg-transparent text-right text-[14px] font-medium text-fg focus:outline-none" />
              </Row>
            )}
          </>
        )}

        {!recording && !manage && (
          <div className="space-y-1.5 px-0.5">
            <div className="flex items-center justify-between gap-2 text-[12px] text-fg-muted">
              <div className="inline-flex rounded-md border border-[var(--line-soft)] p-0.5">
                {(
                  [
                    ['DAY', 'Day'],
                    ['GTC', 'Until cancelled'],
                    ['GTD', 'Until…'],
                  ] as const
                ).map(([t, label]) => (
                  <button
                    key={t}
                    onClick={() => {
                      setTif(t)
                      if (t === 'GTD' && !goodTill) setGoodTill(etNow())
                    }}
                    className={cn('rounded px-2 py-0.5 text-[11px] font-medium', tif === t ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg')}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <label className="flex cursor-pointer items-center gap-1.5 text-[11.5px]" title="Pre-market and after-hours. Limit orders only.">
                <input type="checkbox" checked={ext} onChange={(e) => setExt(e.target.checked)} className="accent-[var(--color-accent)]" />
                Extended hours
              </label>
            </div>
            {tif === 'GTD' && <input value={goodTill} onChange={(e) => setGoodTill(e.target.value)} placeholder="YYYY-MM-DD HH:MM (New York)" className={field} />}
          </div>
        )}

        {/* how it ends: targets and stops */}
        {!recording && buy && (
          <Fold title="Take profit · stop loss" hint="none" count={targets.length + stops.length} open={open.exits} onToggle={() => setOpen((o) => ({ ...o, exits: !o.exits }))}>
            <div className="text-[11px] leading-snug text-fg-faint">A price, or a distance from the entry: “+4%”, “-2%”. Several rows make a ladder — give each its share of the position.</div>
            {targets.map((t, i) => (
              <div key={`t${i}`} className="flex items-center gap-1.5">
                <span className="w-9 shrink-0 text-[11px] font-semibold text-up">TP{i + 1}</span>
                <input value={t.price} onChange={(e) => setTargets((a) => a.map((x, k) => (k === i ? { ...x, price: e.target.value } : x)))} placeholder="price or +%" className={field} />
                <input value={t.pct} onChange={(e) => setTargets((a) => a.map((x, k) => (k === i ? { ...x, pct: e.target.value } : x)))} placeholder={i === targets.length - 1 ? 'rest' : '%'} className={cn(field, 'w-16 shrink-0')} title="Share of the position sold here (%). Empty on the last row = the rest." />
                <button onClick={() => setTargets((a) => a.filter((_, k) => k !== i))} className="shrink-0 rounded p-1 text-fg-subtle hover:text-down"><X size={13} /></button>
              </div>
            ))}
            {stops.map((s, i) => (
              <div key={`s${i}`} className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-9 shrink-0 text-[11px] font-semibold text-down">SL{i + 1}</span>
                  <input value={s.price} onChange={(e) => setStops((a) => a.map((x, k) => (k === i ? { ...x, price: e.target.value } : x)))} placeholder="price or -%" className={field} />
                  <input value={i === stops.length - 1 ? '' : s.pct} disabled={i === stops.length - 1} onChange={(e) => setStops((a) => a.map((x, k) => (k === i ? { ...x, pct: e.target.value } : x)))} placeholder={i === stops.length - 1 ? 'rest' : '%'} className={cn(field, 'w-16 shrink-0 disabled:opacity-60')} title="Share of the position this stop protects (%). The last stop takes the rest." />
                  <button onClick={() => setStops((a) => a.filter((_, k) => k !== i))} className="shrink-0 rounded p-1 text-fg-subtle hover:text-down"><X size={13} /></button>
                </div>
                <div className="flex items-center gap-1.5 pl-[42px]">
                  <select value={s.kind} onChange={(e) => setStops((a) => a.map((x, k) => (k === i ? { ...x, kind: e.target.value as StopRow['kind'] } : x)))} className={cn(select, 'flex-1')}>
                    <option value="stop">Stop (sells at market)</option>
                    <option value="limit">Stop limit (worst price)</option>
                    <option value="trail">Trailing (follows the price)</option>
                  </select>
                  {s.kind !== 'stop' && <input value={s.extra} onChange={(e) => setStops((a) => a.map((x, k) => (k === i ? { ...x, extra: e.target.value } : x)))} placeholder={s.kind === 'limit' ? 'limit' : 'by'} className={cn(field, 'w-16 shrink-0')} />}
                  {s.kind === 'trail' && (
                    <select value={s.trailUnit} onChange={(e) => setStops((a) => a.map((x, k) => (k === i ? { ...x, trailUnit: e.target.value as 'usd' | 'pct' } : x)))} className={select}>
                      <option value="pct">%</option>
                      <option value="usd">$</option>
                    </select>
                  )}
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-1.5">
              {targets.length < 6 && (
                <button onClick={() => setTargets((a) => [...a, { price: '', pct: '' }])} className="inline-flex items-center gap-1 rounded-lg border border-[var(--line-soft)] px-2 py-1 text-[11.5px] text-fg-muted hover:text-fg">
                  <Plus size={11} /> Target
                </button>
              )}
              {stops.length < 6 && (
                <button onClick={() => setStops((a) => [...a, { price: '', pct: '', kind: 'stop', extra: '', trailUnit: 'pct' }])} className="inline-flex items-center gap-1 rounded-lg border border-[var(--line-soft)] px-2 py-1 text-[11.5px] text-fg-muted hover:text-fg">
                  <Plus size={11} /> Stop
                </button>
              )}
              {plan && (plan.risk > 0 || plan.reward > 0) && (
                <span className="num ml-auto text-[11px] text-fg-subtle">
                  {plan.risk > 0 && <span className="text-down">−{fmtMoney(plan.risk)}</span>}
                  {plan.risk > 0 && plan.reward > 0 && ' / '}
                  {plan.reward > 0 && <span className="text-up">+{fmtMoney(plan.reward)}</span>}
                  {plan.risk > 0 && plan.reward > 0 && <span className="text-fg-faint"> · {(plan.reward / plan.risk).toFixed(2)}R</span>}
                </span>
              )}
            </div>
            {exitsOn && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--line-soft)] pt-2 text-[11.5px] text-fg-muted">
                <label className="flex items-center gap-1.5" title="Once the price has covered most of the way to a target, that lot’s stop moves to break-even (fees included).">
                  Break-even insurance
                  <select value={insurance} onChange={(e) => setInsurance(e.target.value as typeof insurance)} className={select}>
                    <option value="default">as in Settings</option>
                    <option value="off">off</option>
                    <option value="75">at 75% of the way</option>
                    <option value="90">at 90%</option>
                    <option value="95">at 95%</option>
                  </select>
                </label>
                <label className="flex cursor-pointer items-center gap-1.5" title="Let the targets and stops work in pre-market and after-hours too.">
                  <input type="checkbox" checked={exitExt} onChange={(e) => setExitExt(e.target.checked)} className="accent-[var(--color-accent)]" />
                  Exits in extended hours
                </label>
              </div>
            )}
          </Fold>
        )}

        {/* what happens afterwards: rules */}
        {!recording && buy && (
          <Fold title="Automation" hint="no rules" count={rules.length} open={open.rules} onToggle={() => setOpen((o) => ({ ...o, rules: !o.rules }))}>
            <div className="text-[11px] leading-snug text-fg-faint">Rules manage the position after the buy — each fires once. Stops only ever move in your favour.</div>
            {rules.map((r, i) => {
              const w = WHEN.find(([k]) => k === r.when)!
              const t = THEN.find(([k]) => k === r.then)!
              const set = (patch: Partial<RuleRow>) => setRules((a) => a.map((x, k) => (k === i ? { ...x, ...patch } : x)))
              return (
                <div key={i} className="space-y-1.5 rounded-lg bg-[var(--color-inset)] p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-9 shrink-0 text-[10.5px] font-semibold uppercase text-fg-faint">When</span>
                    <select value={r.when} onChange={(e) => set({ when: e.target.value as When, whenVal: e.target.value === 'tp' ? '1' : '' })} className={cn(select, 'flex-1')}>
                      {WHEN.map(([k, label]) => (
                        <option key={k} value={k}>{label}</option>
                      ))}
                    </select>
                    {w[2] && <input value={r.whenVal} onChange={(e) => set({ whenVal: e.target.value })} placeholder={w[2]} className={cn(field, r.when === 'time' ? 'w-36' : 'w-20', 'shrink-0')} />}
                    <button onClick={() => setRules((a) => a.filter((_, k) => k !== i))} className="shrink-0 rounded p-1 text-fg-subtle hover:text-down"><X size={13} /></button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-9 shrink-0 text-[10.5px] font-semibold uppercase text-fg-faint">Then</span>
                    <select value={r.then} onChange={(e) => set({ then: e.target.value as Then, thenVal: '' })} className={cn(select, 'flex-1')}>
                      {THEN.map(([k, label]) => (
                        <option key={k} value={k}>{label}</option>
                      ))}
                    </select>
                    {t[2] && <input value={r.thenVal} onChange={(e) => set({ thenVal: e.target.value })} placeholder={t[2]} className={cn(field, r.then === 'notify' ? 'w-32' : 'w-20', 'shrink-0')} />}
                    {r.then === 'trail' && (
                      <select value={r.thenUnit} onChange={(e) => set({ thenUnit: e.target.value as RuleRow['thenUnit'] })} className={select}>
                        <option value="pct">%</option>
                        <option value="usd">$</option>
                        <option value="atr">ATR</option>
                      </select>
                    )}
                  </div>
                </div>
              )
            })}
            {rules.length < 12 && (
              <button onClick={() => setRules((a) => [...a, { when: 'tp', whenVal: '1', then: 'breakeven', thenVal: '', thenUnit: 'pct' }])} className="inline-flex items-center gap-1 rounded-lg border border-[var(--line-soft)] px-2 py-1 text-[11.5px] text-fg-muted hover:text-fg">
                <Plus size={11} /> Rule
              </button>
            )}
          </Fold>
        )}

        {/* notes on the symbol (not orders) */}
        {(holding || recording) && (
          <Fold title="Notes" hint={holding?.stopLoss != null || holding?.takeProfit != null ? 'set' : 'levels to watch'} open={open.notes} onToggle={() => setOpen((o) => ({ ...o, notes: !o.notes }))}>
            <div className="text-[11px] leading-snug text-fg-faint">Levels you keep an eye on for {display} — shown in the table and known to Libera. Notes, not orders.</div>
            <div className="flex gap-2">
              <input value={sl} onChange={(e) => setSl(e.target.value)} placeholder="Stop loss" inputMode="decimal" className={field} />
              <input value={tp} onChange={(e) => setTp(e.target.value)} placeholder="Take profit" inputMode="decimal" className={field} />
            </div>
            {recording && (
              <div className="flex gap-2">
                <input value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Fee" inputMode="decimal" className={field} />
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className={cn(field, 'font-sans')} />
              </div>
            )}
            {holding && (
              <button onClick={() => void setLevels(fullSym, sl ? Number(sl) : null, tp ? Number(tp) : null).then(onChanged)} className="w-full rounded-lg border border-[var(--line)] bg-[var(--color-surface-2)] py-1.5 text-[12px] font-medium text-fg transition-colors hover:bg-[var(--color-surface-3)]">
                Save notes on {display}
              </button>
            )}
          </Fold>
        )}

        <div className="flex items-center justify-between px-1 text-[12px]">
          <span className="text-fg-subtle">{manage ? 'Value managed' : 'Estimated total'}</span>
          <span className="num font-medium text-fg">{fmtMoney(total)}</span>
        </div>
        {manage ? (
          <div className="px-1 text-[11.5px] text-fg-subtle">Nothing is bought: a trade worker takes over these shares and runs their targets, stops and rules. Prices like “+4%” are from the current price.</div>
        ) : (
          asWorker && <div className="px-1 text-[11.5px] text-fg-subtle">Runs as a trade worker: it places the orders, then manages the targets, stops and rules by itself.</div>
        )}
        {problems.map((p) => (
          <div key={p} className="px-1 text-[11.5px] text-down">{p}</div>
        ))}
        {account?.blocked && <div className="px-1 text-[11.5px] text-down">{account.blocked}</div>}
        {account && buy && !manage && account.buyingPower != null && total > account.buyingPower + 0.005 && !account.blocked && <div className="px-1 text-[11.5px] text-down">More than this account’s buying power ({fmtMoney(account.buyingPower)}).</div>}
        {account && !buy && sizeMode === 'shares' && shares > sellable + 1e-9 && <div className="px-1 text-[11.5px] text-down">More than the {qtyText(sellable)} you can sell here.</div>}

        {recording ? (
          <button onClick={() => void record()} disabled={busy || !norm || !(shares > 0) || !(last > 0 || numOf(limit))} className={cn('h-11 w-full rounded-xl text-[14px] font-semibold text-[var(--color-base)] transition-opacity disabled:opacity-40', buy ? 'bg-up' : 'bg-down')}>
            Record {buy ? 'buy' : 'sale'} {display !== '—' ? display : ''}
          </button>
        ) : (
          <button onClick={() => void review()} disabled={busy || !ready} className={cn('h-11 w-full rounded-xl text-[14px] font-semibold text-[var(--color-base)] transition-opacity disabled:opacity-40', manage ? 'bg-[var(--color-accent)]' : buy ? 'bg-up' : 'bg-down')}>
            {busy ? 'Preparing…' : manage ? `Review plan for ${display}` : `Review ${buy ? 'buy' : 'sell'} ${display !== '—' ? display : ''}`}
          </button>
        )}
        {!recording && <div className="px-1 text-center text-[11px] text-fg-faint">Nothing is sent until you approve the card.</div>}
        {msg && <div className={cn('px-1 text-[11.5px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
      </div>

      {/* the system's card for what was filled in: exact orders, risk, checks — and the Approve button */}
      {card && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-3 sm:p-8" onClick={closeCard}>
          <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between text-[12px] text-fg-muted">
              <span>Review — nothing is sent until you approve.</span>
              <button onClick={closeCard} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-fg-subtle hover:text-fg">
                <X size={14} /> Close
              </button>
            </div>
            {card.kind === 'order' ? <OrderCard id={card.id} variant="page" /> : <WorkerCard id={card.id} variant="page" />}
          </div>
        </div>
      )}
    </div>
  )
}
